import { useState, useCallback } from 'react';
import { getIncentiveLeaderboard } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import {
  IndianRupee, RefreshCw, Trophy, AlertTriangle, CheckCircle2,
  TrendingUp, Package, Truck,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IncentiveRow {
  user_id: number;
  user_uuid: string;
  agent_name: string;
  agent_email: string;
  agent_code: string;
  leads: number;
  orders: number;
  revenue: number;
  aov: number;
  conversion_pct: number;
  units: number;
  cod_orders: number;
  partial_orders: number;
  prepaid_orders: number;
  delivered_count: number;
  in_transit_count: number;
  rto_count: number;
  fulfilled_count: number;
  rto_pct: number;
  delivered_units: number;
  spot_days: number;
  spot_incentive: number;
  slab_incentive: number;
  slab_label: string | null;
  total_incentive: number;
  rto_blocked: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const fmtINR = (v: number) =>
  `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

// ─── Component ────────────────────────────────────────────────────────────────

const IncentiveLeaderboard = () => {
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState<IncentiveRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIncentiveLeaderboard({ from_date: fromDate, to_date: toDate });
      if (res.success) {
        setRows(res.data as IncentiveRow[]);
        setLoaded(true);
      } else {
        toastMessage('Failed to load incentive data', 'error');
      }
    } catch {
      toastMessage('Error loading incentive leaderboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Derived totals for summary cards
  const totals = rows.reduce(
    (acc, r) => ({
      orders: acc.orders + r.orders,
      delivered: acc.delivered + r.delivered_count,
      rto: acc.rto + r.rto_count,
      payout: acc.payout + r.total_incentive,
    }),
    { orders: 0, delivered: 0, rto: 0, payout: 0 }
  );

  const overallRtoPct = totals.orders > 0
    ? ((totals.rto / totals.orders) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* ── Header ── */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-amber-500 p-2 rounded-lg">
              <IndianRupee className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Incentive Leaderboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Slab + spot incentives based on delivered orders
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">To</label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

        {/* Summary Cards */}
        {loaded && (
          <div className="grid grid-cols-4 gap-4">
            <SummaryCard
              icon={<Package className="h-5 w-5 text-blue-600" />}
              bg="bg-blue-50 dark:bg-blue-900/20"
              label="Total Orders"
              value={totals.orders.toLocaleString('en-IN')}
            />
            <SummaryCard
              icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
              bg="bg-green-50 dark:bg-green-900/20"
              label="Delivered"
              value={totals.delivered.toLocaleString('en-IN')}
            />
            <SummaryCard
              icon={<TrendingUp className="h-5 w-5 text-red-600" />}
              bg="bg-red-50 dark:bg-red-900/20"
              label={`RTO (${overallRtoPct}%)`}
              value={totals.rto.toLocaleString('en-IN')}
            />
            <SummaryCard
              icon={<IndianRupee className="h-5 w-5 text-amber-600" />}
              bg="bg-amber-50 dark:bg-amber-900/20"
              label="Total Payout"
              value={fmtINR(totals.payout)}
            />
          </div>
        )}

        {/* Empty / loading state */}
        {!loaded && !loading && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <Trophy className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">Select a date range and click Run to load the leaderboard.</p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <RefreshCw className="h-8 w-8 mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading incentive data…</p>
          </div>
        )}

        {/* Table */}
        {loaded && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <Th>#</Th>
                    <Th>Agent</Th>
                    <Th>Leads</Th>
                    <Th>Orders</Th>
                    <Th>Conv%</Th>
                    <Th>Revenue</Th>
                    <Th>AOV</Th>
                    <Th>Units</Th>
                    <Th>Payment Split</Th>
                    <Th>Delivery Status</Th>
                    <Th>Del.Units</Th>
                    <Th>RTO%</Th>
                    <Th>Incentives</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row, idx) => (
                    <AgentRow key={row.user_id} row={row} rank={idx + 1} />
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={13} className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                        No data found for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        {loaded && (
          <div className="flex items-start space-x-6 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-1.5">
              <span className="inline-block w-3 h-3 rounded bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700" />
              <span>Red row = RTO &gt; 15% (slab blocked)</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Truck className="h-3 w-3" />
              <span>Payment: COD - Partial - Prepaid (as %)</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Package className="h-3 w-3" />
              <span>Delivery: Fulfilled - InTransit - Delivered - RTO (as %)</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
    {children}
  </th>
);

const SummaryCard = ({
  icon, bg, label, value,
}: {
  icon: React.ReactNode;
  bg: string;
  label: string;
  value: string;
}) => (
  <div className={`${bg} rounded-xl p-4 border border-transparent`}>
    <div className="flex items-center space-x-3">
      <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">{icon}</div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  </div>
);

const AgentRow = ({ row, rank }: { row: IncentiveRow; rank: number }) => {
  const total = row.orders || 0;

  const codPct   = pct(row.cod_orders, total);
  const partPct  = pct(row.partial_orders, total);
  const prepPct  = pct(row.prepaid_orders, total);

  const fulPct   = pct(row.fulfilled_count, total);
  const itPct    = pct(row.in_transit_count, total);
  const delPct   = pct(row.delivered_count, total);
  const rtoPct   = pct(row.rto_count, total);

  const isRtoHigh = row.rto_blocked;

  const incentiveLabel = (() => {
    const parts: string[] = [];
    if (row.slab_incentive > 0) {
      parts.push(`${fmtINR(row.slab_incentive)} slab`);
    }
    if (row.spot_incentive > 0) {
      parts.push(`${fmtINR(row.spot_incentive)} spot`);
    }
    if (parts.length === 0) return '₹0';
    return parts.join(' + ');
  })();

  const rowBg = isRtoHigh
    ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
    : 'hover:bg-gray-50 dark:hover:bg-gray-700/30';

  return (
    <tr className={`transition-colors ${rowBg}`}>
      {/* Rank */}
      <td className="px-4 py-3 text-gray-400 dark:text-gray-500 font-mono text-xs">
        {rank <= 3
          ? ['🥇', '🥈', '🥉'][rank - 1]
          : rank}
      </td>

      {/* Agent */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          {isRtoHigh && (
            <span title="RTO > 15% — slab blocked">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
            </span>
          )}
          <div>
            <p className="font-medium text-gray-900 dark:text-white text-sm">{row.agent_name}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">{row.agent_code}</p>
          </div>
        </div>
      </td>

      {/* Leads */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-center">{row.leads}</td>

      {/* Orders */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-center font-medium">{row.orders}</td>

      {/* Conv% */}
      <td className="px-4 py-3 text-center">
        <span className={`text-xs font-semibold ${
          row.conversion_pct >= 20
            ? 'text-green-600 dark:text-green-400'
            : row.conversion_pct >= 10
              ? 'text-yellow-600 dark:text-yellow-400'
              : 'text-gray-500 dark:text-gray-400'
        }`}>
          {row.conversion_pct.toFixed(1)}%
        </span>
      </td>

      {/* Revenue */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-right font-medium">
        {fmtINR(row.revenue)}
      </td>

      {/* AOV */}
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-right">
        {fmtINR(row.aov)}
      </td>

      {/* Units */}
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-center">{row.units}</td>

      {/* Payment Split: COD - Partial - Prepaid */}
      <td className="px-4 py-3">
        <div className="text-xs font-mono text-gray-600 dark:text-gray-400 text-center">
          <span className="text-blue-600 dark:text-blue-400">{codPct}</span>
          <span className="text-gray-400 mx-0.5">-</span>
          <span className="text-yellow-600 dark:text-yellow-400">{partPct}</span>
          <span className="text-gray-400 mx-0.5">-</span>
          <span className="text-green-600 dark:text-green-400">{prepPct}</span>
        </div>
        <p className="text-center text-gray-300 dark:text-gray-600 text-xs mt-0.5">COD-Part-Pre</p>
      </td>

      {/* Delivery Status: Fulfilled - InTransit - Delivered - RTO */}
      <td className="px-4 py-3">
        <div className="text-xs font-mono text-gray-600 dark:text-gray-400 text-center">
          <span className="text-gray-500">{fulPct}</span>
          <span className="text-gray-400 mx-0.5">-</span>
          <span className="text-blue-600 dark:text-blue-400">{itPct}</span>
          <span className="text-gray-400 mx-0.5">-</span>
          <span className="text-green-600 dark:text-green-400">{delPct}</span>
          <span className="text-gray-400 mx-0.5">-</span>
          <span className="text-red-500">{rtoPct}</span>
        </div>
        <p className="text-center text-gray-300 dark:text-gray-600 text-xs mt-0.5">Ful-IT-Del-RTO</p>
      </td>

      {/* Delivered Units */}
      <td className="px-4 py-3 text-center">
        <span className={`text-sm font-semibold ${
          row.delivered_units >= 450
            ? 'text-purple-600 dark:text-purple-400'
            : row.delivered_units >= 200
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-500 dark:text-gray-400'
        }`}>
          {row.delivered_units}
        </span>
        {row.slab_label && (
          <p className="text-xs text-gray-400 dark:text-gray-500">{row.slab_label}</p>
        )}
      </td>

      {/* RTO% */}
      <td className="px-4 py-3 text-center">
        <span className={`text-sm font-semibold ${
          isRtoHigh
            ? 'text-red-600 dark:text-red-400'
            : row.rto_pct > 10
              ? 'text-orange-500'
              : 'text-gray-600 dark:text-gray-400'
        }`}>
          {row.rto_pct.toFixed(1)}%
        </span>
      </td>

      {/* Incentives */}
      <td className="px-4 py-3">
        <div className="flex flex-col items-end">
          <span className={`text-sm font-bold ${
            row.total_incentive > 0
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-400 dark:text-gray-500'
          }`}>
            {fmtINR(row.total_incentive)}
          </span>
          {row.total_incentive > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">{incentiveLabel}</span>
          )}
          {row.spot_days > 0 && (
            <span className="text-xs text-indigo-500 dark:text-indigo-400">
              {row.spot_days} spot {row.spot_days === 1 ? 'day' : 'days'}
            </span>
          )}
          {isRtoHigh && row.slab_incentive === 0 && (
            <span className="text-xs text-red-500">slab blocked</span>
          )}
        </div>
      </td>
    </tr>
  );
};

export default IncentiveLeaderboard;
